/**
 * @file useApplicationForm.hook.ts
 * @path apps/website/src/features/recruitment/hooks/useApplicationForm.hook.ts
 */
import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "sonner";
import { Vacancy } from "@sirbastians/types";
import { validateCVFile } from "@/utils";
import {
  jobApplicationSchema,
  JobApplicationFormData,
} from "../recruitment.types";
import { RecruitmentService } from "../recruitment.service";

export const useApplicationForm = () => {
  const [dialogJob, setDialogJob] = useState<Vacancy | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [cvFile, setCvFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState("");

  const form = useForm<JobApplicationFormData>({
    resolver: zodResolver(jobApplicationSchema),
  });

  const openApplication = (job: Vacancy) => setDialogJob(job);

  const closeApplication = () => {
    if (isSubmitting) return;
    form.reset();
    setCvFile(null);
    setFileError("");
    setIsSuccess(false);
    setDialogJob(null);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) {
      setCvFile(null);
      setFileError("");
      return;
    }
    const validation = validateCVFile(file);
    if (!validation.valid) {
      setFileError(validation.error || "Invalid file");
      setCvFile(null);
      return;
    }
    setCvFile(file);
    setFileError("");
  };

  const removeFile = () => {
    setCvFile(null);
    setFileError("");
  };

  const submitApplication = async (data: JobApplicationFormData) => {
    if (!cvFile || !dialogJob) {
      setFileError("Please upload your CV/Resume");
      return;
    }

    setIsSubmitting(true);
    try {
      const cvFileBase64 = await RecruitmentService.convertFileToBase64(cvFile);

      await RecruitmentService.submitApplication({
        ...data,
        jobId: dialogJob.id,
        jobTitle: dialogJob.title,
        jobLocation: dialogJob.location,
        cvFileBase64,
        cvFileName: cvFile.name,
      });

      setIsSuccess(true);
      toast.success("Application Submitted!", {
        description: "We'll review your application and get back to you soon.",
      });
      setTimeout(closeApplication, 3000);
    } catch (error) {
      toast.error("Submission Failed", {
        description: error.message || "Please try again later.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    form,
    dialogJob,
    cvFile,
    fileError,
    isSubmitting,
    isSuccess,
    actions: {
      openApplication,
      closeApplication,
      handleFileChange,
      removeFile,
      submitApplication: form.handleSubmit(submitApplication),
    },
  };
};
